<script>
  import { tick } from 'svelte'

  const AMOUNT_VALID_RE = /^\d+(\.\d{1,2})?$/
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  let {
    amount = $bindable(''),
    selectedPreset = $bindable(null),
    amountEditable = $bindable(false),
    contact = $bindable(),
    options = $bindable(),
    frequency,
    onnext,
    onback,
  } = $props()

  let errors = $state({})
  let phoneEl = $state()
  let editableAmountEl = $state()
  let lastValidAmount = ''
  let amountMask
  let amountSyncing = false

  function clearError(name) {
    if (!errors[name]) return
    const e = { ...errors }
    delete e[name]
    errors = e
  }

  function setError(name, msg) {
    errors = { ...errors, [name]: msg }
  }

  function getAmountError() {
    if (!amount || !AMOUNT_VALID_RE.test(amount) || parseFloat(amount) < 1) {
      return 'Please enter a valid amount of $1 or more.'
    }
    return null
  }

  function getContactError(name) {
    if (name === 'firstName' && !contact.firstName.trim()) return 'First name is required.'
    if (name === 'lastName' && !contact.lastName.trim()) return 'Last name is required.'
    if (name === 'email') {
      if (!contact.email.trim()) return 'Email is required.'
      if (!EMAIL_RE.test(contact.email)) return 'Please enter a valid email address.'
    }
    return null
  }

  function onAmountBlur() {
    if (!amount) {
      amount = lastValidAmount
      clearError('amount')
      amountEditable = false
      return
    }

    const msg = getAmountError()
    if (msg) {
      setError('amount', msg)
    } else {
      clearError('amount')
      if (amount.includes('.')) amount = parseFloat(amount).toFixed(2)
      amountEditable = false
    }
  }

  function onContactBlur(name) {
    const msg = getContactError(name)
    if (msg) setError(name, msg)
  }

  let editableAtPointerDown = false

  function onChangePointerDown() {
    editableAtPointerDown = amountEditable
  }

  async function onChangeClick() {
    if (editableAtPointerDown) {
      // User pressed while editable → this is a Done click.
      // Blur typically fired between pointerdown and click and already
      // ran the validate/collapse logic. If it didn't (focus didn't shift),
      // trigger it explicitly.
      if (amountEditable) editableAmountEl?.blur()
      return
    }
    lastValidAmount = amount
    amountEditable = true
    await tick()
    editableAmountEl?.focus()
  }

  function validate() {
    const e = {}
    const amtMsg = getAmountError()
    if (amtMsg) e.amount = amtMsg
    for (const name of ['firstName', 'lastName', 'email']) {
      const msg = getContactError(name)
      if (msg) e[name] = msg
    }
    errors = e
    return Object.keys(e).length === 0
  }

  function handleNext() {
    if (validate()) onnext()
  }

  $effect(() => {
    if (!phoneEl) return
    let mask
    ;(async () => {
      const { default: IMask } = await import('imask/esm/imask')
      await import('imask/esm/masked/pattern')
      mask = IMask(phoneEl, { mask: '(000) 000-0000' })
    })()
    return () => mask?.destroy()
  })

  $effect(() => {
    if (!editableAmountEl) return
    ;(async () => {
      const { default: IMask } = await import('imask/esm/imask')
      await import('imask/esm/masked/number')
      amountMask = IMask(editableAmountEl, {
        mask: Number,
        scale: 2,
        thousandsSeparator: ',',
        radix: '.',
        mapToRadix: ['.'],
        min: 0,
        normalizeZeros: false,
      })
      amountMask.unmaskedValue = amount
      amountMask.on('accept', () => {
        if (amountSyncing) return
        amount = amountMask.unmaskedValue
        selectedPreset = null
        if (errors.amount) clearError('amount')
      })
    })()
    return () => {
      amountMask?.destroy()
      amountMask = undefined
    }
  })

  // push external amount changes (blur padding, restore) into the mask.
  // read `amount` first so it's tracked even while `amountMask` is still loading.
  $effect(() => {
    const v = amount
    if (amountMask && amountMask.unmaskedValue !== v) {
      amountSyncing = true
      amountMask.unmaskedValue = v
      amountSyncing = false
    }
  })
</script>

<style>
  @reference "@/styles/index.css";

  .input-field {
    @apply focus-within:outline-primary block w-full appearance-none rounded-md bg-white px-5 py-[15px] text-base outline outline-transparent placeholder:text-gray-700/80 xl:text-lg;

    &.has-error {
      @apply outline-error;
    }
  }

  input#editable-amount.has-error {
    @apply outline outline-error;
  }

  .checkboxset li {
    label {
      @apply relative inline-flex cursor-pointer items-baseline text-base transition-colors xl:text-lg;

      &::before {
        @apply relative top-[0.2em] mr-3 size-5 shrink-0 rounded border border-gray-300 bg-white;
        content: "";
      }
    }

    input[type="checkbox"] {
      @apply absolute w-0 opacity-0;

      &:checked ~ label {
        &::before {
          @apply bg-secondary border-secondary border bg-center bg-no-repeat;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 13 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.581.429A1.454 1.454 0 0 0 10.995.11a1.44 1.44 0 0 0-.471.318L4.438 6.486l-1.95-1.964A1.456 1.456 0 1 0 .418 6.571l2.978 3a1.454 1.454 0 0 0 2.057.004l7.122-7.084a1.467 1.467 0 0 0 .01-2.055l-.003-.007Z' fill='%23ffffff'/%3E%3C/svg%3E");
          background-size: 60%;
        }
      }

      &:hover ~ label::before,
      &:focus ~ label::before {
        @apply border-secondary;
      }
    }
  }
</style>

<div>
  <h2 class="font-bold uppercase text-primary text-h4 em-secondary">
    <span>Donation</span> Details
  </h2>

  <p class="mt-1 text-sm opacity-50 xl:text-base">( * ) Denotes required field.</p>
</div>

<fieldset class="mt-8">
  <legend class="font-semibold xl:text-xl">
    Payment Summary

    <button type="button" class="font-medium underline transition-colors text-primary hover:text-secondary" onpointerdown={onChangePointerDown} onclick={onChangeClick}>
      {amountEditable ? 'Done' : 'Change'}
    </button>
  </legend>

  <div class="relative mt-3">
    <label for="editable-amount" class="sr-only">Amount</label>
    <div class="grid absolute top-0 left-0 place-content-center h-full w-[2.5em]" aria-hidden="true">
      <svg fill="none" viewBox="0 0 26 39" class="m-auto w-[0.75em]">
        <path fill="#0448c5" d="M11.655 38.52v-3.87C4.32 34.2.18 30.6 0 24.525h6.12c.405 3.15 2.07 4.77 5.535 5.13v-8.55c-.09 0-.18 0-.225-.045C5.94 19.8.855 18.315.855 11.88c0-5.805 4.5-8.91 10.8-9.225V0h2.7v2.7c6.03.54 9.855 3.87 10.26 9.54H18.63c-.315-2.52-1.98-4.005-4.275-4.455v7.785c.09.045.18.045.27.045 5.805 1.17 10.755 2.97 10.755 9.36 0 6.03-4.635 9.405-11.025 9.72v3.825zm7.425-12.78c0-2.295-1.755-3.195-4.725-3.96v7.875c2.61-.27 4.725-1.44 4.725-3.915M7.11 11.205c0 2.07 1.44 2.97 4.545 3.78V7.65C8.82 7.92 7.11 9.225 7.11 11.205"/>
      </svg>
    </div>
    <input
      id="editable-amount"
      bind:this={editableAmountEl}
      type="text"
      inputmode="decimal"
      class="block pr-5 w-full font-bold bg-white rounded-md pl-[2em] py-[15px] text-body-md text-primary"
      class:has-error={!!errors.amount}
      aria-invalid={!!errors.amount}
      aria-describedby={errors.amount ? 'editable-amount-error' : undefined}
      disabled={!amountEditable}
      readonly={!amountEditable}
      onfocus={() => clearError('amount')}
      onblur={onAmountBlur}
    />
  </div>

  {#if errors.amount}
    <p id="editable-amount-error" class="form-field-message bad">{errors.amount}</p>
  {/if}
</fieldset>

<fieldset class="mt-8">
  <legend class="font-semibold xl:text-xl">
    Contact Information
  </legend>

  <div class="grid gap-4 mt-3 md:grid-cols-2">
    <div>
      <label for="firstName" class="sr-only">First name</label>
      <input
        id="firstName"
        type="text"
        name="firstName"
        class="input-field"
        class:has-error={!!errors.firstName}
        aria-invalid={!!errors.firstName}
        aria-describedby={errors.firstName ? 'firstName-error' : undefined}
        placeholder="First Name*"
        bind:value={contact.firstName}
        onfocus={() => clearError('firstName')}
        onblur={() => onContactBlur('firstName')}
      />
      {#if errors.firstName}
        <p id="firstName-error" class="form-field-message bad">{errors.firstName}</p>
      {/if}
    </div>

    <div>
      <label for="lastName" class="sr-only">Last name</label>
      <input
        id="lastName"
        type="text"
        name="lastName"
        class="input-field"
        class:has-error={!!errors.lastName}
        aria-invalid={!!errors.lastName}
        aria-describedby={errors.lastName ? 'lastName-error' : undefined}
        placeholder="Last Name*"
        bind:value={contact.lastName}
        onfocus={() => clearError('lastName')}
        onblur={() => onContactBlur('lastName')}
      />
      {#if errors.lastName}
        <p id="lastName-error" class="form-field-message bad">{errors.lastName}</p>
      {/if}
    </div>

    <div>
      <label for="email" class="sr-only">Email</label>
      <input
        id="email"
        type="email"
        name="email"
        class="input-field"
        class:has-error={!!errors.email}
        aria-invalid={!!errors.email}
        aria-describedby={errors.email ? 'email-error' : undefined}
        placeholder="Email*"
        bind:value={contact.email}
        onfocus={() => clearError('email')}
        onblur={() => onContactBlur('email')}
      />
      {#if errors.email}
        <p id="email-error" class="form-field-message bad">{errors.email}</p>
      {/if}
    </div>

    <div>
      <label for="phone" class="sr-only">Phone</label>
      <input
        id="phone"
        bind:this={phoneEl}
        type="tel"
        name="phone"
        class="input-field"
        placeholder="Phone"
        value={contact.phone}
        oninput={(e) => (contact.phone = e.target.value)}
      />
    </div>

    <div class="col-span-full">
      <label for="businessName" class="sr-only">Business name</label>
      <input id="businessName" type="text" name="businessName" class="input-field" placeholder="Business Name" bind:value={contact.businessName} />
    </div>
  </div>
</fieldset>

<fieldset class="mt-8">
  <legend class="font-semibold xl:text-xl">
    Options
  </legend>

  <ul class="mt-3 checkboxset">
    <li>
      <input id="option-processingFee" type="checkbox" name="processingFee" bind:checked={options.processingFee} />
      <label for="option-processingFee">
        Pay processing fee as part of donation
      </label>
    </li>

    <li>
      <input id="option-newsletter" type="checkbox" name="newsletter" bind:checked={options.newsletter} />
      <label for="option-newsletter">
        Subscribe to Newsletter/Mailings
      </label>
    </li>

    <li>
      <input id="option-volunteer" type="checkbox" name="volunteer" bind:checked={options.volunteer} />
      <label for="option-volunteer">
        Send me information about volunteering
      </label>
    </li>
  </ul>
</fieldset>

<div class="my-8 space-y-4 text-center">
  <button type="button" class="mt-8 w-full btn btn-fill-secondary" onclick={handleNext}>Next</button>
  <button type="button" class="inline-block p-4 underline transition-colors xl:text-xl text-primary hover:text-secondary" onclick={onback}>Back</button>
</div>
